import { useEffect, useState } from 'react';
import { nowSlot } from '@betreuung/shared';

/**
 * A clock that ticks once a minute.
 *
 * The header shows the current time, and the running-assignment card has to move
 * on when a slot ends. Deriving from a ticking value keeps that automatic without
 * refetching anything — the data does not change, only which part of it is "now".
 *
 * The interval realigns to the next whole minute so the displayed time never
 * lags behind the wall clock by up to a minute.
 */
export function useNow(): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let interval: number | undefined;
    const tick = () => setNow(new Date());

    const msToNextMinute = 60_000 - (Date.now() % 60_000);
    const timeout = window.setTimeout(() => {
      tick();
      interval = window.setInterval(tick, 60_000);
    }, msToNextMinute);

    // Coming back from a locked screen must not wait for the next tick.
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearTimeout(timeout);
      if (interval !== undefined) window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return now;
}

/** The running 30-minute slot, derived from {@link useNow}. */
export function useNowSlot(now: Date): number {
  return nowSlot(now);
}
